import React, { useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Activity, AlertCircle, CheckCircle, Info, Pause, Play, Trash2 } from 'lucide-react';
import { Spinner } from '../../components/common/LoadingState';

interface QueueStatus {
  success: boolean;
  message: string;
  data?: any;
  status?: string;
}

const AdminQueuePage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null);
  const [showClearForm, setShowClearForm] = useState(false);
  const [clearDomain, setClearDomain] = useState('');
  const [clearEmail, setClearEmail] = useState('');
  const [includeFailed, setIncludeFailed] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.dhassembly.dhing.com.co/api';
  const token = localStorage.getItem('accessToken');

  const handlePauseQueue = async () => {
    setLoading(true);
    setQueueStatus(null);
    try {
      const response = await fetch(`${apiUrl}/admin/queue/pause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setQueueStatus({
        success: response.ok,
        message: data.message || data.error || 'Error al pausar la cola',
        ...data,
      });
    } catch (error: any) {
      setQueueStatus({
        success: false,
        message: `Error: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResumeQueue = async () => {
    setLoading(true);
    setQueueStatus(null);
    try {
      const response = await fetch(`${apiUrl}/admin/queue/resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setQueueStatus({
        success: response.ok,
        message: data.message || data.error || 'Error al reanudar la cola',
        ...data,
      });
    } catch (error: any) {
      setQueueStatus({
        success: false,
        message: `Error: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearEmailJobs = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setQueueStatus(null);

    if (!clearDomain && !clearEmail) {
      setQueueStatus({
        success: false,
        message: 'Debes especificar un dominio o email para limpiar',
      });
      setLoading(false);
      return;
    }

    try {
      const payload: any = { includeFailed };
      if (clearDomain) payload.domain = clearDomain.toLowerCase().trim();
      if (clearEmail) payload.email = clearEmail.toLowerCase().trim();

      const response = await fetch(`${apiUrl}/admin/queue/clear-email-jobs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      setQueueStatus({
        success: response.ok,
        message: data.message || data.error || 'Error al limpiar la cola',
        ...data,
      });

      if (response.ok) {
        setClearDomain('');
        setClearEmail('');
        setShowClearForm(false);
      }
    } catch (error: any) {
      setQueueStatus({
        success: false,
        message: `Error: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-3xl font-bold text-gray-900">Gestión de Colas</h1>
                <p className="mt-2 text-gray-600">
                  Controla el procesamiento de correos y administra jobs pendientes.
                </p>
              </div>
            </div>
          </div>

          {/* Status Message */}
          {queueStatus && (
            <div className={`mb-6 flex items-start gap-3 rounded-lg border p-4 ${
              queueStatus.success
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}>
              {queueStatus.success ? (
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              )}
              <div>
                <p className={`font-medium ${
                  queueStatus.success ? 'text-green-900' : 'text-red-900'
                }`}>
                  {queueStatus.message}
                </p>
                {queueStatus.data && (
                  <pre className="mt-2 text-xs overflow-auto bg-black/5 p-2 rounded max-h-40">
                    {JSON.stringify(queueStatus.data, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          )}

          {/* Queue Control */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="inline-flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Activity className="h-5 w-5 text-slate-600" aria-hidden="true" />
                  <span>Control de Cola de Emails</span>
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Pausa o reanuda el worker sin eliminar jobs.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handlePauseQueue}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-orange-600 text-white font-medium rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <Pause className="h-4 w-4 mr-2" aria-hidden="true" />
                )}
                <span>Pausar</span>
              </button>
              <button
                onClick={handleResumeQueue}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <Play className="h-4 w-4 mr-2" aria-hidden="true" />
                )}
                <span>Reanudar</span>
              </button>
            </div>
          </div>

          {/* Clear Email Jobs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">Limpiar Jobs de Email</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Elimina jobs por dominio o por email exacto. Úsalo solo si es necesario.
                </p>
              </div>
              <button
                onClick={() => setShowClearForm(!showClearForm)}
                className="shrink-0 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                {showClearForm ? 'Cancelar' : 'Mostrar'}
              </button>
            </div>

            {showClearForm && (
              <form onSubmit={handleClearEmailJobs} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dominio (ej: loadtest.com)
                    </label>
                    <input
                      type="text"
                      value={clearDomain}
                      onChange={(e) => setClearDomain(e.target.value)}
                      placeholder="loadtest.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Limpiará todos los emails que terminen con @dominio
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Exacto (ej: user@test.com)
                    </label>
                    <input
                      type="email"
                      value={clearEmail}
                      onChange={(e) => setClearEmail(e.target.value)}
                      placeholder="user@test.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Limpiará solo este email específico
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="includeFailed"
                    checked={includeFailed}
                    onChange={(e) => setIncludeFailed(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="includeFailed" className="text-sm text-gray-700">
                    Incluir jobs fallidos también
                  </label>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <Spinner size="sm" className="mr-2" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                    )}
                    <span>Limpiar Jobs</span>
                  </button>
                </div>
              </form>
            )}

            <div className="mt-4 flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-700" aria-hidden="true" />
              <p>
                <strong>Advertencia:</strong> Esta operación eliminará jobs pendientes de la cola. Usa con cuidado.
              </p>
            </div>
          </div>

          {/* Info Section */}
          <div className="mt-8 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <h3 className="mb-2 inline-flex items-center gap-2 font-semibold text-blue-900">
              <Info className="h-4 w-4 text-blue-800" aria-hidden="true" />
              <span>Información</span>
            </h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li>• <strong>Pausar:</strong> Detiene el procesamiento de jobs sin eliminarlos</li>
              <li>• <strong>Reanudar:</strong> Continúa procesando jobs pendientes</li>
              <li>• <strong>Limpiar:</strong> Elimina jobs específicos de la cola (waiting/delayed/failed)</li>
              <li>• Actualmente el worker rechaza envíos a usuarios eliminados/inactivos</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminQueuePage;
